import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Columns2, DoorClosed, Inbox, Rows2, Square, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { useCennikMaterialy, useKovaniePodlaKodu } from "@/api/hooks/useCennik";
import {
  hrubkaMaterialu,
  nesuladHrubkySlotu,
  predvolenaHrubkaDosky,
  useDostupneHrubky,
} from "@/api/hooks/useDostupneHrubky";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { HrubkaDoskySelect } from "@/components/komponent/HrubkaDoskySelect";
import { VyberKovaniaDialog } from "@/components/dielec/VyberKovaniaDialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { formatEur } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  POLICA_SETBACK_VLOZENE,
  ZAVES_KATALOG,
  PODLOZKA_KATALOG,
  generujKorpus,
  hlbkaMedzisteny,
  najdiOtvor,
  normalizujOtvorTypy,
  normalizujTypCela,
  novyOtvor,
  odpocetPolice,
  pocetNoh,
  pocetZavesov,
  policeZaVlozenymi,
  rozmerCelnejPlochy,
  rozmerDvierok,
  upravDelenie,
  upravOtvor,
  vlozKomponent,
  vyberNlVysuvu,
  vypocitajOtvory,
  vyskaBokuZasuvky,
  zrusDelenie,
  type KomponentKind,
} from "@/lib/korpus/generator";
import {
  MEDZERA_CIEL,
  PREDSADENIE_CELA,
  nulovePresahy,
} from "@/lib/korpus/types";
import type {
  ChrbatTyp,
  Delenie,
  DelenieRef,
  DoskaId,
  KorpusParams,
  Otvor,
  Presahy,
  Rect,
  TypCela,
  TypKonstrukcie,
} from "@/lib/korpus/types";
import { KorpusPresety } from "@/components/komponent/KorpusPresety";
import type { Dielec, Material, MaterialSlot, Zostava } from "@/api/schemas";

/**
 * Korpus Builder v2 (F1) — parametrický korpus s klikateľnými otvormi
 * a doskami: nárys + bokorys s reálnymi hrúbkami, medzisteny (stred /
 * vzdialenosť od dna/stropu/boku, editovateľné klikom), police, presahy
 * dosiek, nohy s auto počtom. Presné číselné vstupy v mm — žiadne slidery.
 */

const TYP_LABELS: Record<TypKonstrukcie, string> = {
  vlozeny_plat: "Vložený plát",
  nalozeny_plat: "Naložený plát",
};
const TYP_HINT: Record<TypKonstrukcie, string> = {
  vlozeny_plat: "plát a dno medzi bokmi — bok plná výška",
  nalozeny_plat: "plát a dno cez boky — bok = výška − 2t",
};

const DOSKA_LABELS: Record<DoskaId, string> = {
  plat: "Plát",
  dno: "Dno",
  bok_l: "Bok ľavý",
  bok_p: "Bok pravý",
};

/** Výber v náryse: otvor (delenie/police) alebo doska (presahy). */
type Vyber = { kind: "otvor" | "doska"; id: string } | null;

/** Zbaliteľná sekcia ľavého panela parametrov. */
function Sekcia({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [otvorene, setOtvorene] = useState(defaultOpen);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOtvorene((o) => !o)}
        aria-expanded={otvorene}
        className="flex w-full items-center justify-between px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {title}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            otvorene ? "" : "-rotate-90",
          )}
        />
      </button>
      {otvorene ? (
        <div className="space-y-2 px-2.5 pb-2.5">{children}</div>
      ) : null}
    </div>
  );
}

export interface KorpusBuilderV2Props {
  zakazkaId: string;
  zostavaId: string;
  sloty: MaterialSlot[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Predvyplnená konfigurácia (tvar buildKonfig) — komponent z knižnice
   *  alebo šablóna; aplikuje sa pri otvorení dialógu. */
  initialKonfig?: Record<string, unknown> | null;
  /** Zavolá sa po úspešnom vytvorení dielcov (pred zavretím dialógu). */
  onCreated?: () => void;
}

export function KorpusBuilderV2({
  zakazkaId,
  zostavaId,
  sloty,
  open,
  onOpenChange,
  initialKonfig,
  onCreated,
}: KorpusBuilderV2Props): JSX.Element {
  const createDielec = useCreateDielec(zakazkaId, zostavaId);
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  // Dosky z Nastavení — ponúkajú sa, keď slot nemá plošný materiál
  const hrubky = useDostupneHrubky();

  /** Hrúbka plošného materiálu slotu — z nej sa korpus reže (KOLO 168). */
  function hrubkaSlotu(id: string): number | null {
    return hrubkaMaterialu(sloty.find((s) => s.id === id)?.material ?? null);
  }

  // ── parametre (presné mm — textové inputy) ──
  const [sirka, setSirka] = useState("600");
  const [vyska, setVyska] = useState("720");
  const [hlbka, setHlbka] = useState("560");
  // Hrúbka podľa materiálu slotu (KOLO 168) — natvrdo 18 korpus zo
  // škárovky 27 mm nevložil, server dielce odmietol (422)
  const [t, setT] = useState(() =>
    String(predvolenaHrubkaDosky(hrubkaSlotu(sloty[0]?.id ?? ""), hrubky)),
  );
  const [typ, setTyp] = useState<TypKonstrukcie>("vlozeny_plat");
  const [chrbat, setChrbat] = useState<ChrbatTyp>("hdf");
  const [slotId, setSlotId] = useState(sloty[0]?.id ?? "");
  const [presahy, setPresahy] = useState<Presahy>(() => nulovePresahy());
  const [nohyOn, setNohyOn] = useState(true);
  const [nohyVyska, setNohyVyska] = useState("100");
  const [nohyPocet, setNohyPocet] = useState(""); // "" = auto
  const [nohyMat, setNohyMat] = useState<ProduktKovania | null>(null);
  const [nohyPickerOpen, setNohyPickerOpen] = useState(false);
  const [zavesMat, setZavesMat] = useState<ProduktKovania | null>(null);
  const [zavesPickerOpen, setZavesPickerOpen] = useState(false);
  const [vysuvMat, setVysuvMat] = useState<ProduktKovania | null>(null);
  const [vysuvPickerOpen, setVysuvPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── strom otvorov + výber ──
  const [root, setRoot] = useState<Otvor>(() => novyOtvor());
  const [sel, setSel] = useState<Vyber>(null);
  // režim vkladania z palety (klik na komponent → klik do otvoru)
  const [vkladany, setVkladany] = useState<KomponentKind | null>(null);
  // formulár úpravy existujúcej medzisteny
  const [editRef, setEditRef] = useState<DelenieRef>("stred");
  const [editMm, setEditMm] = useState("0");

  useEffect(() => {
    if (open) {
      const o = novyOtvor();
      setRoot(o);
      setSel({ kind: "otvor", id: o.id });
      const prvySlot = sloty[0]?.id ?? "";
      setSlotId(prvySlot);
      // Hrúbka ide so slotom; konfigurácia nižšie ju môže prepísať
      setT(String(predvolenaHrubkaDosky(hrubkaSlotu(prvySlot), hrubky)));
      setPresahy(nulovePresahy());
      setVkladany(null);
      // komponent z knižnice / predvolená konfigurácia
      if (initialKonfig) applyKonfig(initialKonfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape ukončí režim vkladania
  useEffect(() => {
    if (!vkladany) return;
    function esc(e: KeyboardEvent): void {
      if (e.key === "Escape") setVkladany(null);
    }
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [vkladany]);

  function handlePlace(otvorId: string): void {
    if (!vkladany) return;
    setRoot((r) => vlozKomponent(r, otvorId, vkladany));
    setSel({ kind: "otvor", id: otvorId });
    // medzistena sa vkladá raz; police/zásuvky sa dajú klikať opakovane
    if (vkladany === "medzistena_v" || vkladany === "medzistena_z") {
      setVkladany(null);
    }
  }

  // default nohy: prvý obľúbený z kategórie Nohy
  // Rodič skupiny vráti aj podskupiny (stolové, rektifikačné…) — server
  const nohyDefaultQuery = useCennikMaterialy({
    sekcia: "kovanie",
    vyber: true,
    je_oblubene: true,
    kategoria: "KOVANIE_NOHA",
    per_page: 1,
  });
  useEffect(() => {
    const m = nohyDefaultQuery.data?.data?.[0];
    if (m && !nohyMat) setNohyMat(produktKovania(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nohyDefaultQuery.data]);

  // závesy: default AUTO podľa reálnych dielenských kódov (56-3550/
  // 56-3690 + podložka 56-0248) — dohľadané nižšie podľa kusovníka;
  // zavesMat je len ručný override cez picker

  // default výsuv: prvý obľúbený z kategórie Výsuvy
  const vysuvDefaultQuery = useCennikMaterialy({
    sekcia: "kovanie",
    vyber: true,
    je_oblubene: true,
    kategoria: "KOVANIE_VYSUV",
    per_page: 1,
  });
  useEffect(() => {
    const m = vysuvDefaultQuery.data?.data?.[0];
    if (m && !vysuvMat) setVysuvMat(produktKovania(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vysuvDefaultQuery.data]);

  // ── uložené konfigurácie (šablóny typu korpus) ──
  function buildKonfig(): Record<string, unknown> {
    return {
      verzia: 1,
      params: {
        sirka,
        vyska,
        hlbka,
        t,
        typ,
        chrbat,
        presahy,
        nohy: {
          enabled: nohyOn,
          vyska: nohyVyska,
          pocet: nohyPocet,
          material: nohyMat,
        },
        zaves: zavesMat,
        vysuv: vysuvMat,
        // slot per-varianta — ukladá sa kód, pri načítaní sa mapuje
        slot_kod: sloty.find((s) => s.id === slotId)?.slot_kod ?? null,
      },
      root,
    };
  }

  function applyKonfig(konfig: Record<string, unknown>): void {
    const p = (konfig.params ?? {}) as Record<string, unknown>;
    if (p.sirka != null) setSirka(String(p.sirka));
    if (p.vyska != null) setVyska(String(p.vyska));
    if (p.hlbka != null) setHlbka(String(p.hlbka));
    if (p.typ) setTyp(p.typ as TypKonstrukcie);
    if (p.chrbat) setChrbat(p.chrbat as ChrbatTyp);
    if (p.presahy) setPresahy(p.presahy as Presahy);
    const nohy = (p.nohy ?? {}) as Record<string, unknown>;
    setNohyOn(Boolean(nohy.enabled ?? true));
    if (nohy.vyska != null) setNohyVyska(String(nohy.vyska));
    setNohyPocet(nohy.pocet != null ? String(nohy.pocet) : "");
    if (nohy.material) {
      setNohyMat(nohy.material as ProduktKovania);
    }
    if (p.zaves) {
      setZavesMat(p.zaves as ProduktKovania);
    }
    if (p.vysuv) {
      setVysuvMat(p.vysuv as ProduktKovania);
    }
    // slot podľa kódu — ak v tejto variante nie je, ostáva aktuálny
    const slotKod = p.slot_kod as string | null | undefined;
    const slot = slotKod ? sloty.find((s) => s.slot_kod === slotKod) : null;
    if (slot) setSlotId(slot.id);
    // Hrúbka z konfigurácie ostáva, aj keď nesedí s materiálom slotu —
    // výber hrúbky ju ukáže s upozornením a Vytvoriť počká (KOLO 168)
    const tKonfig = parseFloat(String(p.t));
    if (Number.isFinite(tKonfig)) {
      setT(String(tKonfig));
    } else if (slot) {
      const z = hrubkaMaterialu(slot.material ?? null);
      if (z != null) setT(String(z));
    }
    if (konfig.root) {
      // legacy typy čiel ("nasadene") z uložených konfigov → naložené
      const r = normalizujOtvorTypy(konfig.root as Otvor);
      setRoot(r);
      setSel({ kind: "otvor", id: r.id });
    }
  }

  const params: KorpusParams = useMemo(
    () => ({
      sirka: parseFloat(sirka) || 0,
      vyska: parseFloat(vyska) || 0,
      hlbka: parseFloat(hlbka) || 0,
      t: parseFloat(t) || 18,
      typ,
      chrbat,
      nohy: {
        enabled: nohyOn,
        vyskaMm: parseFloat(nohyVyska) || 100,
        material: nohyMat,
        pocetOverride: nohyPocet ? parseInt(nohyPocet, 10) : null,
      },
      presahy,
      zaves: zavesMat,
      vysuv: vysuvMat,
    }),
    [sirka, vyska, hlbka, t, typ, chrbat, nohyOn, nohyVyska, nohyPocet, nohyMat, presahy, zavesMat, vysuvMat],
  );

  const { rects, chyby: otvorChyby } = useMemo(
    () => vypocitajOtvory(params, root),
    [params, root],
  );
  const vysledok = useMemo(() => generujKorpus(params, root), [params, root]);

  // dohľadanie kovania podľa dielenských kódov (search_text obsahuje
  // twd/demos/daex/häfele ID) — len kódy, ktoré kusovník reálne používa
  const kodyVKusovniku = useMemo(
    () => new Set(vysledok.kovanie.map((k) => k.katalog).filter(Boolean)),
    [vysledok],
  );
  const matNalozene = useKovaniePodlaKodu(
    ZAVES_KATALOG.nalozene, kodyVKusovniku.has(ZAVES_KATALOG.nalozene));
  const matVlozene = useKovaniePodlaKodu(
    ZAVES_KATALOG.vlozene, kodyVKusovniku.has(ZAVES_KATALOG.vlozene));
  const matPolonalozene = useKovaniePodlaKodu(
    ZAVES_KATALOG.polonalozene, kodyVKusovniku.has(ZAVES_KATALOG.polonalozene));
  const matPodlozka = useKovaniePodlaKodu(
    PODLOZKA_KATALOG, kodyVKusovniku.has(PODLOZKA_KATALOG));
  const kodMaterial: Record<string, Material | null> = {
    [ZAVES_KATALOG.nalozene]: matNalozene,
    [ZAVES_KATALOG.vlozene]: matVlozene,
    [ZAVES_KATALOG.polonalozene]: matPolonalozene,
    [PODLOZKA_KATALOG]: matPodlozka,
  };
  // kovanie s doplneným materiálom z cenníka + cenou (ručný výber má
  // prednosť; cena orientačný NÁKUP za kus)
  const kovanieVysledne = useMemo(
    () =>
      vysledok.kovanie.map((k) => {
        if (k.materialId || !k.katalog) {
          const cena =
            k.materialId === zavesMat?.id
              ? (zavesMat?.cena ?? null)
              : k.materialId === nohyMat?.id
                ? (nohyMat?.cena ?? null)
                : k.materialId === vysuvMat?.id
                  ? (vysuvMat?.cena ?? null)
                  : null;
          return { ...k, cena };
        }
        const m = kodMaterial[k.katalog];
        return m
          ? {
              ...k,
              materialId: m.id,
              nazov: m.nazov,
              cena: parseFloat(String(m.nakup_cena)) || 0,
            }
          : { ...k, cena: null };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vysledok, matNalozene, matVlozene, matPolonalozene, matPodlozka, zavesMat, nohyMat, vysuvMat],
  );

  const selOtvor = sel?.kind === "otvor" ? najdiOtvor(root, sel.id) : null;
  const selRect = selOtvor ? rects.get(selOtvor.id) ?? null : null;
  const selDoska = sel?.kind === "doska" ? (sel.id as DoskaId) : null;

  // predvyplň edit formulár pri výbere rozdeleného otvoru / medzisteny
  useEffect(() => {
    if (selOtvor?.delenie) {
      setEditRef(selOtvor.delenie.ref);
      setEditMm(String(selOtvor.delenie.mm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selOtvor?.id, selOtvor?.delenie?.ref, selOtvor?.delenie?.mm]);

  // materiál slotu pre cenu (orientačná: nákup×(1+réžia)+práca za m²)
  const slotMat = sloty.find((s) => s.id === slotId)?.material ?? null;
  // Hrúbka dosiek musí sedieť s plošným materiálom slotu, inak server
  // dielce odmietne (422) — upozornenie a Vytvoriť počká (KOLO 168)
  const slotZ = hrubkaMaterialu(slotMat);
  const hrubkaNesedi = nesuladHrubkySlotu(params.t, slotZ);

  /** Zmena slotu nastaví hrúbku jeho materiálu — ako dielec (KOLO 159). */
  function zmenSlot(id: string): void {
    setSlotId(id);
    const z = hrubkaSlotu(id);
    if (z != null) setT(String(z));
  }

  const orientacnaCena = useMemo(() => {
    if (!slotMat) return null;
    // orientačný NÁKUP (presnú výrobnú cenu spočíta backend po vytvorení)
    const m2cena = parseFloat(String(slotMat.nakup_cena ?? "0")) || 0;
    let suma = 0;
    for (const d of vysledok.dielce) {
      if (d.z === 4 && d.nazov.startsWith("chrbat")) continue; // HDF/sololit iná sadzba
      suma += ((d.x * d.y) / 1e6) * m2cena * d.ks;
    }
    for (const k of kovanieVysledne) {
      suma += (k.cena ?? 0) * k.ks;
    }
    return suma;
  }, [vysledok, slotMat, kovanieVysledne]);

  /** Validácia osi delenia v otvore — vráti chybovú hlášku alebo null. */
  function osChyba(rect: Rect, smer: Delenie["smer"], ref: DelenieRef, mm: number): string | null {
    const os = ref === "stred" ? (smer === "vodorovne" ? rect.h : rect.w) / 2 : mm;
    const rozmer = smer === "vodorovne" ? rect.h : rect.w;
    const osAbs = ref === "od_stropu" || ref === "od_boku_p" ? rozmer - os : os;
    if (osAbs < params.t / 2 || osAbs > rozmer - params.t / 2) {
      return `Pozícia musí byť medzi ${Math.ceil(params.t / 2)} a ${Math.floor(rozmer - params.t / 2)} mm.`;
    }
    return null;
  }


  function handleUpravDelenie(): void {
    if (!selOtvor?.delenie || !selRect) return;
    const mm = parseFloat(editMm) || 0;
    const chyba = osChyba(selRect, selOtvor.delenie.smer, editRef, mm);
    if (chyba) {
      toast({ variant: "destructive", title: "Medzistena mimo otvoru", description: chyba });
      return;
    }
    setRoot((r) => upravDelenie(r, selOtvor.id, editRef, mm));
  }

  function setPresah(doska: DoskaId, pole: "a" | "b" | "predny" | "zadny", v: string): void {
    const mm = parseFloat(v) || 0;
    setPresahy((p) => ({ ...p, [doska]: { ...p[doska], [pole]: Math.max(0, mm) } }));
  }

  async function handleCreate(): Promise<void> {
    if (vysledok.chyby.length > 0 || hrubkaNesedi) return;
    const zakazka = zakazkaQuery.data?.data;
    const zostava = ((zakazka?.varianty?.[0]?.zostavy ?? []) as Zostava[])
      .concat(
        ((zakazka?.varianty ?? []) as { zostavy?: Zostava[] }[])
          .flatMap((v) => (v.zostavy ?? []) as Zostava[]),
      )
      .find((z) => z.id === zostavaId);
    const existing = ((zostava?.dielce ?? []) as Dielec[]).map((d) => ({
      kod: d.kod,
    }));
    const zostavaKod = zostava?.kod ?? "K";
    // sekvencia kódov: {zostavaKod}_p{n}, {n+1}, …
    const prefix = `${zostavaKod}_p`;
    const used = new Set(
      existing
        .map((d) => (d.kod?.startsWith(prefix) ? Number(d.kod.slice(prefix.length)) : NaN))
        .filter((n) => !Number.isNaN(n)),
    );
    let n = 1;
    const nextKod = (): string => {
      while (used.has(n)) n += 1;
      used.add(n);
      return `${prefix}${n}`;
    };

    setSubmitting(true);
    try {
      let poradie = existing.length;
      for (const d of vysledok.dielce) {
        const kod = nextKod();
        const vSlote = !(d.z === 4 && d.nazov.startsWith("chrbat"));
        // Uni doska smer vlákien nemá (KOLO 144) — natvrdo X tu dávalo
        // skryté X, s ktorým porez neotáčal a Cut Rite dostal textúru 1
        const maStrukturu = vSlote && slotMat ? slotMat.smer_vlakien_default !== "0" : true;
        const res = await createDielec.mutateAsync({
          kod,
          nazov_strojovy: d.nazov,
          pocet_ks: d.ks,
          x_mm: String(d.x),
          y_mm: String(d.y),
          z_mm: String(d.z),
          // HDF/sololit chrbát (4 mm) nepatrí do slotu korpusu
          material_slot_id: vSlote ? slotId || null : null,
          material_id_direct: null,
          operacia: null,
          nadmiera_x_mm: "5.00",
          nadmiera_y_mm: "5.00",
          // CNC nadmiera len kde patrí (KONSTRUKCIE.md §2) — police,
          // dvierka a čelá sú len porez
          nadmiera_aktivna: d.cnc,
          // Builder dielec nemá výrobný postup, takže CNC identitu musí
          // niesť override — od KOLA 108 ju odvodzuje `Operation.je_cnc`
          // a bez tohto by dielec stratil minimum upnutia aj združovanie.
          ide_na_cnc: d.cnc,
          smer_vlakien: maStrukturu ? (d.smer ?? "X") : "0",
          hrana_x1_auto: true,
          hrana_x2_auto: true,
          hrana_y1_auto: true,
          hrana_y2_auto: true,
          poradie: poradie++,
        });
        if (d.operacia) {
          // operácia = vnorený riadok (množstvo z rozmerov rodiča)
          await createDielec.mutateAsync({
            kod: `${kod}_${d.operacia}`,
            nazov_strojovy: d.operacia,
            parent_dielec_id: res.data.id,
            operacia: d.operacia,
            pocet_ks: 1,
            x_mm: null,
            y_mm: null,
            z_mm: null,
            material_slot_id: null,
            material_id_direct: null,
            nadmiera_x_mm: "0",
            nadmiera_y_mm: "0",
            nadmiera_aktivna: false,
            smer_vlakien: "0",
            hrana_x1_auto: false,
            hrana_x2_auto: false,
            hrana_y1_auto: false,
            hrana_y2_auto: false,
            poradie: poradie++,
          });
        }
      }
      for (const k of kovanieVysledne) {
        await createDielec.mutateAsync({
          kod: nextKod(),
          nazov_strojovy: k.nazov,
          pocet_ks: k.ks,
          x_mm: null,
          y_mm: null,
          z_mm: null,
          material_slot_id: null,
          material_id_direct: k.materialId,
          nadmiera_x_mm: "0",
          nadmiera_y_mm: "0",
          nadmiera_aktivna: false,
          smer_vlakien: "0",
          hrana_x1_auto: false,
          hrana_x2_auto: false,
          hrana_y1_auto: false,
          hrana_y2_auto: false,
          poradie: poradie++,
        });
      }
      toast({
        title: "Korpus vytvorený",
        description: `${vysledok.dielce.length} dielcov + ${kovanieVysledne.length} kovanie`,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri vytváraní korpusu",
        description: extractErrorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  const selPresah = selDoska ? presahy[selDoska] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1240px] w-[96vw] max-h-[94vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle>Builder korpusu</DialogTitle>
            <DialogDescription className="sr-only">
              Zostavenie korpusu — otvory, police, dvierka, zásuvky a kovanie.
            </DialogDescription>
            <KorpusPresety getKonfig={buildKonfig} onLoad={applyKonfig} />
          </div>
        </DialogHeader>

        <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)_290px]">
          {/* ── ĽAVÝ PANEL — parametre v sekciách ── */}
          <div className="space-y-2.5 text-sm">
            <Sekcia title="Rozmery">
              <div className="grid grid-cols-3 gap-2">
                <NumInput label="Šírka" value={sirka} onChange={setSirka} />
                <NumInput label="Výška" value={vyska} onChange={setVyska} />
                <NumInput label="Hĺbka" value={hlbka} onChange={setHlbka} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <HrubkaDoskySelect
                  label="Hrúbka dosky"
                  ariaLabel="Hrúbka dosky"
                  labelClassName="text-xs"
                  triggerClassName="h-8"
                  value={t}
                  onChange={setT}
                  matZ={slotZ}
                  hrubky={hrubky}
                />
                <div>
                  <Label className="text-xs">Materiál (slot)</Label>
                  <Select value={slotId} onValueChange={zmenSlot}>
                    <SelectTrigger className="h-8" aria-label="Materiál">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {sloty.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.slot_kod} {s.material?.nazov ?? s.label ?? ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hrubkaNesedi ? (
                <p className="rounded-md border border-destructive bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
                  {hrubkaNesedi}
                </p>
              ) : null}
            </Sekcia>

            <Sekcia title="Konštrukcia">
              <div className="space-y-1">
                {(Object.keys(TYP_LABELS) as TypKonstrukcie[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTyp(k)}
                    aria-pressed={typ === k}
                    className={cn(
                      "block w-full rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
                      typ === k
                        ? "border-primary bg-tandem-red-light font-semibold"
                        : "hover:bg-accent",
                    )}
                  >
                    {TYP_LABELS[k]}
                    <span className="block text-[10px] font-normal text-muted-foreground">
                      {TYP_HINT[k]}
                    </span>
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-xs">Chrbát</Label>
                <Select value={chrbat} onValueChange={(v) => setChrbat(v as typeof chrbat)}>
                  <SelectTrigger className="h-8" aria-label="Chrbát">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hdf">HDF 4 mm v drážke</SelectItem>
                    <SelectItem value="sololit">Sololit pribíjaný (V−10 × Š−10)</SelectItem>
                    <SelectItem value="ltd">LTD vložený (V−2t × Š−2t)</SelectItem>
                    <SelectItem value="none">Bez chrbta</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Hĺbka políc podľa chrbta: HDF −18, sololit −6, LTD −
                  {odpocetPolice("ltd", params.t)} (za vloženými dvierkami
                  ďalších −20).
                </p>
              </div>
            </Sekcia>

            <Sekcia title="Nohy">
              <label className="flex items-center gap-2 text-xs font-semibold">
                <input
                  type="checkbox"
                  checked={nohyOn}
                  onChange={(e) => setNohyOn(e.target.checked)}
                  className="h-3.5 w-3.5 accent-primary"
                />
                Korpus stojí na nohách
              </label>
              {nohyOn ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <NumInput label="Výška nôh" value={nohyVyska} onChange={setNohyVyska} />
                    <div>
                      <Label className="text-xs">
                        Počet <span className="text-muted-foreground">(auto {pocetNoh(params.sirka)})</span>
                      </Label>
                      <Input
                        value={nohyPocet}
                        onChange={(e) => setNohyPocet(e.target.value)}
                        placeholder="auto"
                        inputMode="numeric"
                        className="h-8"
                        aria-label="Počet nôh"
                      />
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Produkt: </span>
                    {nohyMat ? (nohyMat.alias || nohyMat.nazov) : "— (bez ceny)"}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ml-1 h-6 px-2 text-xs"
                      onClick={() => setNohyPickerOpen(true)}
                    >
                      Zmeniť
                    </Button>
                  </div>
                </>
              ) : null}
            </Sekcia>

            <Sekcia title="Kovanie" defaultOpen={false}>
              <div className="space-y-1">
                <p className="text-xs font-semibold">Závesy dvierok</p>
                <div className="text-xs">
                  <span className="text-muted-foreground">Produkt: </span>
                  {zavesMat ? (
                    <>
                      {zavesMat.alias || zavesMat.nazov}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-1 h-6 px-2 text-xs"
                        onClick={() => setZavesMat(null)}
                        title="Vrátiť automatický výber podľa typu dvierok"
                      >
                        Auto
                      </Button>
                    </>
                  ) : (
                    "auto podľa typu dvierok"
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-1 h-6 px-2 text-xs"
                    onClick={() => setZavesPickerOpen(true)}
                  >
                    Zmeniť
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Auto kódy z dielne: naložené {ZAVES_KATALOG.nalozene},
                  vložené {ZAVES_KATALOG.vlozene} + krížová podložka{" "}
                  {PODLOZKA_KATALOG} 1:1. Počet auto podľa hmotnosti krídla
                  (2/3/4; nad 600 mm +1), ručne pri dvierkach.
                </p>
              </div>
              <div className="space-y-1 border-t pt-2">
                <p className="text-xs font-semibold">Výsuvy zásuviek</p>
                <div className="text-xs">
                  <span className="text-muted-foreground">Produkt: </span>
                  {vysuvMat ? (vysuvMat.alias || vysuvMat.nazov) : "— (bez ceny)"}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-1 h-6 px-2 text-xs"
                    onClick={() => setVysuvPickerOpen(true)}
                  >
                    Zmeniť
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Dĺžka NL auto podľa hĺbky korpusu (NL + 4 mm ≤ hĺbka),
                  1 pár na zásuvku. Zásuvky pridáš na otvore v náryse.
                </p>
              </div>
            </Sekcia>

            {vysledok.varovania.map((v, i) => (
              <p key={i} className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                {v}
              </p>
            ))}
            {[...vysledok.chyby, ...otvorChyby].map((c, i) => (
              <p key={i} className="rounded-md border border-destructive bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
                {c}
              </p>
            ))}
          </div>

          {/* ── STRED — NÁRYS ── */}
          <div>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Komponenty — klikni na komponent a potom do otvoru
            </h4>
            <KorpusPaleta vkladany={vkladany} onPick={setVkladany} />
            <NarysSvg
              params={params}
              root={root}
              rects={rects}
              sel={sel}
              onSelect={setSel}
              placing={vkladany !== null}
              onPlace={handlePlace}
            />
            {/* panel akcií podľa výberu */}
            {selDoska && selPresah ? (
              <div className="mt-2 rounded-md border bg-background-alt/40 p-2.5 text-xs">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="font-semibold">
                    Doska: {DOSKA_LABELS[selDoska]} — presahy (mm)
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() =>
                      setPresahy((p) => ({ ...p, [selDoska]: { a: 0, b: 0, predny: 0, zadny: 0 } }))
                    }
                  >
                    Vynulovať
                  </Button>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <PresahInput
                    label={selDoska === "bok_l" || selDoska === "bok_p" ? "Dole" : "Ľavý"}
                    value={selPresah.a}
                    onChange={(v) => setPresah(selDoska, "a", v)}
                  />
                  <PresahInput
                    label={selDoska === "bok_l" || selDoska === "bok_p" ? "Hore" : "Pravý"}
                    value={selPresah.b}
                    onChange={(v) => setPresah(selDoska, "b", v)}
                  />
                  <PresahInput
                    label="Predný"
                    value={selPresah.predny}
                    onChange={(v) => setPresah(selDoska, "predny", v)}
                  />
                  <PresahInput
                    label="Zadný"
                    value={selPresah.zadny}
                    onChange={(v) => setPresah(selDoska, "zadny", v)}
                  />
                  <p className="ml-auto text-[10px] text-muted-foreground">
                    Presah predlžuje dosku oproti korpusu — vnútro sa nemení.
                  </p>
                </div>
              </div>
            ) : selOtvor && selRect ? (
              <div className="mt-2 rounded-md border bg-background-alt/40 p-2.5 text-xs">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="font-semibold">
                    Otvor {Math.round(selRect.w)} × {Math.round(selRect.h)} mm
                  </span>
                  {selOtvor.delenie ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs hover:text-destructive"
                      onClick={() => setRoot((r) => zrusDelenie(r, selOtvor.id))}
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      Zmazať medzistenu
                    </Button>
                  ) : null}
                </div>
                {!selOtvor.delenie ? (
                  <div className="flex flex-wrap items-end gap-3">
                    {selOtvor.police === 0 &&
                    !selOtvor.dvierka &&
                    !selOtvor.zasuvky ? (
                      <p className="pb-1 text-muted-foreground">
                        Prázdny otvor — vlož komponent z palety nad
                        nárysom (medzistena, polica, dvierka, zásuvka).
                      </p>
                    ) : null}

                    {selOtvor.police > 0 ? (
                      <div className="flex items-end gap-1">
                        <div className="w-20">
                          <Label className="text-[10px]">Police (ks)</Label>
                          <Input
                            value={String(selOtvor.police)}
                            onChange={(e) => {
                              const nP = Math.max(0, parseInt(e.target.value, 10) || 0);
                              setRoot((r) => upravOtvor(r, selOtvor.id, (o) => ({ ...o, police: nP })));
                            }}
                            inputMode="numeric"
                            className="h-7 text-xs"
                            aria-label="Počet políc v otvore"
                          />
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 hover:text-destructive"
                          aria-label="Odstrániť police"
                          title="Odstrániť police z otvoru"
                          onClick={() =>
                            setRoot((r) => upravOtvor(r, selOtvor.id, (o) => ({ ...o, police: 0 })))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}

                    {selOtvor.dvierka ? (
                      <div className="flex items-end gap-1.5">
                        <div>
                          <Label className="text-[10px]">Dvierka — krídla</Label>
                          <Select
                            value={String(selOtvor.dvierka.kridla)}
                            onValueChange={(v) => {
                              const kridla = parseInt(v, 10) as 1 | 2;
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) =>
                                  o.dvierka
                                    ? { ...o, dvierka: { ...o.dvierka, kridla } }
                                    : o,
                                ),
                              );
                            }}
                          >
                            <SelectTrigger className="h-7 w-24 text-xs" aria-label="Počet krídel dvierok">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 krídlo</SelectItem>
                              <SelectItem value="2">2 krídla</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Typ</Label>
                          <Select
                            value={normalizujTypCela(selOtvor.dvierka.typ)}
                            onValueChange={(v) => {
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) =>
                                  o.dvierka
                                    ? { ...o, dvierka: { ...o.dvierka, typ: v as TypCela } }
                                    : o,
                                ),
                              );
                            }}
                          >
                            <SelectTrigger className="h-7 w-36 text-xs" aria-label="Typ dvierok">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nalozene" title="čelná plocha −2 mm/strana, 20 mm pred korpusom">naložené</SelectItem>
                              <SelectItem value="vlozene" title="otvor −2 mm/strana, v rovine hrany">vložené</SelectItem>
                              <SelectItem value="polonalozene" title="polovičné prekrytie t/2−1">polonaložené</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24">
                          <Label className="text-[10px]">
                            Závesy/krídlo
                            {(() => {
                              const dv = rozmerDvierok(selRect, selOtvor.dvierka, {
                                t: params.t,
                                innerW: params.sirka - 2 * params.t,
                                innerH: params.vyska - 2 * params.t,
                              });
                              return (
                                <span className="text-muted-foreground">
                                  {" "}(auto {pocetZavesov(dv.sirkaKridla, dv.vyska, params.t)})
                                </span>
                              );
                            })()}
                          </Label>
                          <Input
                            value={
                              selOtvor.dvierka.zavesy != null
                                ? String(selOtvor.dvierka.zavesy)
                                : ""
                            }
                            placeholder="auto"
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              const parsed = raw === "" ? null : Math.max(1, parseInt(raw, 10) || 1);
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) =>
                                  o.dvierka
                                    ? { ...o, dvierka: { ...o.dvierka, zavesy: parsed } }
                                    : o,
                                ),
                              );
                            }}
                            inputMode="numeric"
                            className="h-7 text-xs"
                            aria-label="Počet závesov na krídlo"
                          />
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 hover:text-destructive"
                          aria-label="Odstrániť dvierka"
                          title="Odstrániť dvierka z otvoru"
                          onClick={() =>
                            setRoot((r) => upravOtvor(r, selOtvor.id, (o) => ({ ...o, dvierka: undefined })))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}

                    {selOtvor.zasuvky ? (
                      <div className="flex items-end gap-1.5">
                        <div className="w-20">
                          <Label className="text-[10px]">Zásuvky (ks)</Label>
                          <Input
                            value={String(selOtvor.zasuvky.pocet)}
                            onChange={(e) => {
                              const n = Math.max(0, parseInt(e.target.value, 10) || 0);
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) => ({
                                  ...o,
                                  zasuvky:
                                    n === 0
                                      ? undefined
                                      : {
                                          pocet: n,
                                          vyskaBoku: o.zasuvky?.vyskaBoku ?? null,
                                          celoTyp: o.zasuvky?.celoTyp ?? "vlozene",
                                        },
                                })),
                              );
                            }}
                            inputMode="numeric"
                            className="h-7 text-xs"
                            aria-label="Počet zásuviek v otvore"
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-[10px]">
                            Výška boku{" "}
                            <span className="text-muted-foreground">
                              (auto{" "}
                              {vyskaBokuZasuvky(
                                selRect.h,
                                selOtvor.zasuvky.pocet,
                                normalizujTypCela(selOtvor.zasuvky.celoTyp),
                              )}
                              )
                            </span>
                          </Label>
                          <Input
                            value={
                              selOtvor.zasuvky.vyskaBoku != null
                                ? String(selOtvor.zasuvky.vyskaBoku)
                                : ""
                            }
                            placeholder="auto"
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              const v =
                                raw === ""
                                  ? null
                                  : Math.max(40, parseInt(raw, 10) || 120);
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) =>
                                  o.zasuvky
                                    ? { ...o, zasuvky: { ...o.zasuvky, vyskaBoku: v } }
                                    : o,
                                ),
                              );
                            }}
                            inputMode="numeric"
                            className="h-7 text-xs"
                            aria-label="Výška boku zásuvky"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Čelá</Label>
                          <Select
                            value={normalizujTypCela(selOtvor.zasuvky.celoTyp)}
                            onValueChange={(v) => {
                              setRoot((r) =>
                                upravOtvor(r, selOtvor.id, (o) =>
                                  o.zasuvky
                                    ? { ...o, zasuvky: { ...o.zasuvky, celoTyp: v as TypCela } }
                                    : o,
                                ),
                              );
                            }}
                          >
                            <SelectTrigger className="h-7 w-36 text-xs" aria-label="Typ čiel zásuviek">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nalozene" title="čelná plocha −2 mm/strana, 20 mm pred korpusom">naložené</SelectItem>
                              <SelectItem value="vlozene" title="otvor −2 mm/strana, v rovine hrany">vložené</SelectItem>
                              <SelectItem value="polonalozene" title="polovičné prekrytie t/2−1">polonaložené</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 hover:text-destructive"
                          aria-label="Odstrániť zásuvky"
                          title="Odstrániť zásuvky z otvoru"
                          onClick={() =>
                            setRoot((r) => upravOtvor(r, selOtvor.id, (o) => ({ ...o, zasuvky: undefined })))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <span className="pb-1.5 text-muted-foreground">
                      {selOtvor.delenie.smer === "vodorovne" ? "Vodorovná" : "Zvislá"} medzistena:
                    </span>
                    <div>
                      <Label className="text-[10px]">Pozícia</Label>
                      <Select value={editRef} onValueChange={(v) => setEditRef(v as DelenieRef)}>
                        <SelectTrigger className="h-7 w-36 text-xs" aria-label="Referencia pozície medzisteny">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stred">na stred</SelectItem>
                          {selOtvor.delenie.smer === "vodorovne" ? (
                            <>
                              <SelectItem value="od_dna">od dna otvoru</SelectItem>
                              <SelectItem value="od_stropu">od stropu otvoru</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="od_boku_l">od ľavého boku</SelectItem>
                              <SelectItem value="od_boku_p">od pravého boku</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    {editRef !== "stred" ? (
                      <div className="w-20">
                        <Label className="text-[10px]">mm (os)</Label>
                        <Input
                          value={editMm}
                          onChange={(e) => setEditMm(e.target.value)}
                          inputMode="decimal"
                          className="h-7 text-xs"
                          aria-label="Nová pozícia medzisteny v mm"
                        />
                      </div>
                    ) : null}
                    <Button type="button" size="sm" className="h-7 text-xs" onClick={handleUpravDelenie}>
                      Použiť
                    </Button>
                    <p className="ml-auto pb-1.5 text-[10px] text-muted-foreground">
                      Pod-otvory vyberieš klikom priamo v náryse.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                Klikni do otvoru v náryse — pridáš medzistenu (na stred alebo presnú
                vzdialenosť), police… Klik na dosku (plát/dno/bok) nastaví presahy.
              </p>
            )}
          </div>

          {/* ── PRAVÝ PANEL — bokorys + kusovník ── */}
          <div className="space-y-3">
            <div>
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Bokorys (rez)
              </h4>
              <BokorysSvg params={params} root={root} rects={rects} />
            </div>
            <div className="text-xs">
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Kusovník ({vysledok.dielce.length + vysledok.kovanie.length})
              </h4>
              <table className="w-full" style={{ fontVariantNumeric: "tabular-nums" }}>
                <tbody>
                  {vysledok.dielce.map((d, i) => (
                    <tr key={i}>
                      <td className="py-0.5 pr-2">
                        {d.nazov}
                        {d.ks > 1 ? ` ×${d.ks}` : ""}
                        {d.operacia ? (
                          <span className="ml-1 text-[10px] text-primary">+{d.operacia}</span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap text-right font-mono text-muted-foreground">
                        {d.x} × {d.y} × {d.z}
                      </td>
                    </tr>
                  ))}
                  {kovanieVysledne.map((k, i) => {
                    const nenajdene = !k.materialId && !!k.katalog;
                    return (
                      <tr key={`k${i}`}>
                        <td
                          className={cn(
                            "py-0.5 pr-2",
                            nenajdene ? "text-destructive" : "text-primary",
                          )}
                          title={nenajdene ? `Kód ${k.katalog} sa nenašiel v cenníku — dielec vznikne bez materiálu` : k.poznamka}
                        >
                          ⚙ {k.nazov}
                          {nenajdene ? " (nenájdené v cenníku)" : ""}
                        </td>
                        <td className="whitespace-nowrap text-right font-mono text-muted-foreground">
                          {k.ks} ks
                          {k.cena != null ? (
                            <span className="ml-1 text-foreground/70">
                              {formatEur(k.cena * k.ks)}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {orientacnaCena != null ? (
                <p className="mt-1.5 border-t pt-1.5 text-right">
                  Nákup orientačne{" "}
                  <b className="font-mono">{formatEur(orientacnaCena)}</b>
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Zrušiť
          </Button>
          <Button
            onClick={() => void handleCreate()}
            disabled={
              submitting ||
              vysledok.chyby.length > 0 ||
              otvorChyby.length > 0 ||
              hrubkaNesedi !== null
            }
            title={hrubkaNesedi ?? undefined}
          >
            {submitting ? "Vytvára sa…" : "Vytvoriť korpus"}
          </Button>
        </DialogFooter>

        {/* Produkty kovania — okno výberu ako všade (KOLO 202): začne
            v kategórii produktu s ★ Obľúbenými, klik označí, Vybrať alebo
            dvojklik potvrdí */}
        <VyberKovaniaDialog
          open={nohyPickerOpen}
          onClose={() => setNohyPickerOpen(false)}
          nadpis="Vyber produkt nôh"
          currentId={nohyMat?.id ?? null}
          pociatocnaKategoria="KOVANIE_NOHA"
          onSelect={(m) => {
            setNohyMat(produktKovania(m));
            setNohyPickerOpen(false);
          }}
        />
        <VyberKovaniaDialog
          open={vysuvPickerOpen}
          onClose={() => setVysuvPickerOpen(false)}
          nadpis="Vyber výsuv zásuviek"
          currentId={vysuvMat?.id ?? null}
          pociatocnaKategoria="KOVANIE_VYSUV"
          onSelect={(m) => {
            setVysuvMat(produktKovania(m));
            setVysuvPickerOpen(false);
          }}
        />
        <VyberKovaniaDialog
          open={zavesPickerOpen}
          onClose={() => setZavesPickerOpen(false)}
          nadpis="Vyber záves dvierok"
          currentId={zavesMat?.id ?? null}
          pociatocnaKategoria="KOVANIE_ZAVES"
          onSelect={(m) => {
            setZavesMat(produktKovania(m));
            setZavesPickerOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Vybraný produkt kovania v builderi — ide do kusovníka (názov, cena)
 * aj do uloženej konfigurácie. Alias je len na ukázanie (KOLO 176);
 * staré konfigurácie ho nemajú a ukážu pravý názov.
 */
type ProduktKovania = { id: string; nazov: string; cena: number; alias?: string | null };

function produktKovania(m: Material): ProduktKovania {
  return {
    id: m.id,
    nazov: m.nazov,
    cena: parseFloat(String(m.nakup_cena)) || 0,
    alias: m.alias?.trim() || null,
  };
}

function NumInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        className="h-8 font-mono"
        aria-label={label}
      />
    </div>
  );
}

function PresahInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: string) => void;
}): JSX.Element {
  const [text, setText] = useState(String(value));
  // sync z rodiča (Vynulovať, prepnutie dosky) bez rušenia písania
  useEffect(() => {
    if ((parseFloat(text) || 0) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="w-16">
      <Label className="text-[10px]">{label}</Label>
      <Input
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value);
        }}
        inputMode="decimal"
        className="h-7 text-xs font-mono"
        aria-label={`Presah ${label.toLowerCase()}`}
      />
    </div>
  );
}

// ─── SVG nárys ───────────────────────────────────────────────

const WOOD = "hsl(33 45% 72%)";
const WOOD_EDGE = "hsl(30 35% 40%)";
const SEL_STROKE = "hsl(350 75% 45%)";

function Doska({ x, y, w, h }: Rect): JSX.Element {
  return (
    <rect
      x={x} y={y} width={Math.max(w, 0)} height={Math.max(h, 0)}
      fill={WOOD} stroke={WOOD_EDGE} strokeWidth="1"
    />
  );
}

/** Doska v mm súradniciach (y od podlahy hore) — pre bbox + klik. */
interface DoskaMm {
  id: DoskaId;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Vypočíta mm obdĺžniky 4 dosiek vrátane presahov (nárys). */
function doskyNarysu(params: KorpusParams): DoskaMm[] {
  const { sirka: W, vyska: H, t, typ, nohy, presahy: pr } = params;
  const LH = nohy.enabled ? nohy.vyskaMm : 0;
  const plnaSirka = typ === "nalozeny_plat";
  const bokY0 = typ === "vlozeny_plat" ? LH : LH + t;
  const bokDlzka = typ === "vlozeny_plat" ? H : H - 2 * t;
  const platX0 = plnaSirka ? 0 : t;
  const platW = plnaSirka ? W : W - 2 * t;
  return [
    {
      id: "plat",
      x: platX0 - pr.plat.a,
      y: LH + H - t,
      w: platW + pr.plat.a + pr.plat.b,
      h: t,
    },
    {
      id: "dno",
      x: platX0 - pr.dno.a,
      y: LH,
      w: platW + pr.dno.a + pr.dno.b,
      h: t,
    },
    {
      id: "bok_l",
      x: 0,
      y: bokY0 - pr.bok_l.a,
      w: t,
      h: bokDlzka + pr.bok_l.a + pr.bok_l.b,
    },
    {
      id: "bok_p",
      x: W - t,
      y: bokY0 - pr.bok_p.a,
      w: t,
      h: bokDlzka + pr.bok_p.a + pr.bok_p.b,
    },
  ];
}

/** Karty palety — klik vyberie komponent, klik do otvoru ho vloží. */
const PALETA: Array<{
  kind: KomponentKind;
  label: string;
  Icon: typeof Rows2;
}> = [
  { kind: "medzistena_v", label: "Medzistena ―", Icon: Rows2 },
  { kind: "medzistena_z", label: "Medzistena ｜", Icon: Columns2 },
  { kind: "polica", label: "Polica", Icon: Square },
  { kind: "dvierka1", label: "Dvierka 1-kr.", Icon: DoorClosed },
  { kind: "dvierka2", label: "Dvierka 2-kr.", Icon: DoorClosed },
  { kind: "zasuvka", label: "Zásuvka", Icon: Inbox },
];

function KorpusPaleta({
  vkladany,
  onPick,
}: {
  vkladany: KomponentKind | null;
  onPick: (k: KomponentKind | null) => void;
}): JSX.Element {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
      {PALETA.map(({ kind, label, Icon }) => (
        <Button
          key={kind}
          type="button"
          size="sm"
          variant={vkladany === kind ? "default" : "outline"}
          className="h-8 px-2.5 text-xs font-medium"
          aria-pressed={vkladany === kind}
          title={
            vkladany === kind
              ? "Klikni do otvoru v náryse — Escape zruší"
              : `Vlož komponent: ${label}`
          }
          onClick={() => onPick(vkladany === kind ? null : kind)}
        >
          <Icon className="mr-1 h-3.5 w-3.5" />
          {label}
        </Button>
      ))}
      {vkladany ? (
        <span className="text-[10px] text-primary">
          klikni do otvoru (Esc zruší)
        </span>
      ) : null}
    </div>
  );
}

function NarysSvg({
  params,
  root,
  rects,
  sel,
  onSelect,
  placing = false,
  onPlace,
}: {
  params: KorpusParams;
  root: Otvor;
  rects: Map<string, Rect>;
  sel: Vyber;
  onSelect: (v: Vyber) => void;
  /** Režim vkladania z palety — klik na otvor volá onPlace. */
  placing?: boolean;
  onPlace?: (otvorId: string) => void;
}): JSX.Element {
  const { sirka: W, vyska: H, t, nohy } = params;
  const LH = nohy.enabled ? nohy.vyskaMm : 0;
  const pad = 42;
  const VW = 560;
  const VH = 600;

  const doskyMm = doskyNarysu(params);
  // bbox v mm vrátane presahov
  const minX = Math.min(0, ...doskyMm.map((d) => d.x));
  const maxX = Math.max(W, ...doskyMm.map((d) => d.x + d.w));
  const minY = Math.min(0, ...doskyMm.map((d) => d.y));
  const maxY = Math.max(H + LH, ...doskyMm.map((d) => d.y + d.h));
  const s = Math.min(
    (VW - 2 * pad) / Math.max(maxX - minX, 1),
    (VH - 2 * pad) / Math.max(maxY - minY, 1),
  );
  const X = (mm: number): number => pad + (mm - minX) * s;
  // y=0 = podlaha (spodok nôh); SVG y rastie dole
  const Y = (mm: number): number => VH - pad - (mm - minY) * s;
  const px = (mm: number): number => mm * s;
  const celoCtx = { t, innerW: W - 2 * t, innerH: H - 2 * t };

  // klikateľné dosky
  const dosky = doskyMm.map((d) => {
    const isSel = sel?.kind === "doska" && sel.id === d.id;
    return (
      <rect
        key={d.id}
        x={X(d.x)}
        y={Y(d.y + d.h)}
        width={px(d.w)}
        height={px(d.h)}
        fill={WOOD}
        stroke={isSel ? SEL_STROKE : WOOD_EDGE}
        strokeWidth={isSel ? 2 : 1}
        className="cursor-pointer"
        role="button"
        aria-label={`Doska ${DOSKA_LABELS[d.id]}`}
        onClick={() => onSelect({ kind: "doska", id: d.id })}
      />
    );
  });

  // medzisteny + police + klikateľné otvory (inner origin = (t, LH+t))
  const inner = (r: Rect): Rect => ({
    x: X(t + r.x),
    y: Y(LH + t + r.y + r.h),
    w: px(r.w),
    h: px(r.h),
  });
  const medzisteny: JSX.Element[] = [];
  const police: JSX.Element[] = [];
  const otvory: JSX.Element[] = [];

  function walk(o: Otvor): void {
    const r = rects.get(o.id);
    if (!r) return;
    if (o.delenie) {
      const d = o.delenie;
      const ir = inner(r);
      // medzistena rect
      const osOk = (() => {
        const roz = d.smer === "vodorovne" ? r.h : r.w;
        const os0 =
          d.ref === "stred" ? roz / 2 : d.ref === "od_stropu" || d.ref === "od_boku_p" ? roz - d.mm : d.mm;
        return os0 >= t / 2 && os0 <= roz - t / 2 ? os0 : null;
      })();
      if (osOk != null) {
        const selMedzistena = sel?.kind === "otvor" && sel.id === o.id;
        const mProps = {
          fill: WOOD,
          stroke: selMedzistena ? SEL_STROKE : WOOD_EDGE,
          strokeWidth: selMedzistena ? 2 : 1,
          className: "cursor-pointer",
          role: "button" as const,
          "aria-label": `Medzistena ${d.smer === "vodorovne" ? "vodorovná" : "zvislá"}`,
          onClick: () => onSelect({ kind: "otvor", id: o.id }),
        };
        if (d.smer === "vodorovne") {
          medzisteny.push(
            <rect
              key={`m${o.id}`}
              x={ir.x} y={Y(LH + t + r.y + osOk + t / 2)} width={px(r.w)} height={px(t)}
              {...mProps}
            />,
          );
        } else {
          medzisteny.push(
            <rect
              key={`m${o.id}`}
              x={X(t + r.x + osOk - t / 2)} y={ir.y} width={px(t)} height={px(r.h)}
              {...mProps}
            />,
          );
        }
        walk(d.deti[0]);
        walk(d.deti[1]);
      }
      return;
    }
    const ir = inner(r);
    // police
    for (let i = 1; i <= o.police; i++) {
      const py = r.y + (r.h * i) / (o.police + 1);
      police.push(
        <Doska key={`p${o.id}${i}`} x={ir.x} y={Y(LH + t + py + t / 2)} w={px(r.w)} h={px(t)} />,
      );
    }
    // dvierka — krídla na skutočnej pozícii: naložené/polonaložené
    // presahujú otvor (prekryjú boky/medzisteny), vložené sú vnútri
    if (o.dvierka) {
      const typ = normalizujTypCela(o.dvierka.typ);
      const n = o.dvierka.kridla;
      const { x0, y0, sirkaKridla, vyska } = rozmerDvierok(r, o.dvierka, celoCtx);
      const presah = typ !== "vlozene";
      const ky = Y(LH + t + y0 + vyska);
      const kh = px(vyska);
      for (let i = 0; i < n; i++) {
        const kx = X(t + x0 + i * (sirkaKridla + MEDZERA_CIEL));
        const kw = px(sirkaKridla);
        // pánty: 1 krídlo vľavo; 2 krídla — krajné pánty
        const pantyVpravo = n === 2 && i === 1;
        const px0 = pantyVpravo ? kx + kw : kx; // strana pántov
        const px1 = pantyVpravo ? kx : kx + kw; // protiľahlá hrana
        police.push(
          <g key={`dv${o.id}${i}`} pointerEvents="none">
            <rect
              x={kx} y={ky} width={kw} height={kh}
              fill={WOOD} fillOpacity={presah ? "0.5" : "0.35"}
              stroke={WOOD_EDGE} strokeWidth={presah ? "1.5" : "1"}
            />
            <path
              d={`M ${px0} ${ky} L ${px1} ${ky + kh / 2} L ${px0} ${ky + kh}`}
              fill="none" stroke={WOOD_EDGE} strokeWidth="1"
              strokeDasharray="4 3"
            />
          </g>,
        );
      }
      police.push(
        <text
          key={`dvt${o.id}`}
          x={ir.x + ir.w / 2} y={ir.y + ir.h - 6}
          textAnchor="middle" fontSize="9" fill={WOOD_EDGE}
          fontFamily="sans-serif" pointerEvents="none"
        >
          {n === 2 ? "2× dvierka" : "dvierka"}
        </text>,
      );
    }
    // zásuvky — čelá na skutočnej pozícii podľa typu (ako dvierka)
    if (o.zasuvky && o.zasuvky.pocet > 0) {
      const typ = normalizujTypCela(o.zasuvky.celoTyp);
      const n = o.zasuvky.pocet;
      const plocha = rozmerCelnejPlochy(r, typ, celoCtx);
      const vyskaCela = (plocha.h - (n - 1) * MEDZERA_CIEL) / n;
      const presah = typ !== "vlozene";
      const cx = X(t + plocha.x0);
      const cw = px(plocha.w);
      for (let i = 0; i < n; i++) {
        // i = 0 → horné čelo
        const hornaHrana = plocha.y0 + plocha.h - i * (vyskaCela + MEDZERA_CIEL);
        const cy = Y(LH + t + hornaHrana);
        const ch = px(vyskaCela);
        police.push(
          <g key={`za${o.id}${i}`} pointerEvents="none">
            <rect
              x={cx} y={cy} width={cw} height={ch}
              fill={WOOD} fillOpacity={presah ? "0.5" : "0.35"}
              stroke={WOOD_EDGE} strokeWidth={presah ? "1.5" : "1"}
            />
            <line
              x1={cx + cw / 2 - cw / 10}
              y1={cy + ch / 2}
              x2={cx + cw / 2 + cw / 10}
              y2={cy + ch / 2}
              stroke={WOOD_EDGE} strokeWidth="2"
            />
          </g>,
        );
      }
    }
    // klikateľný otvor
    const isSel = sel?.kind === "otvor" && sel.id === o.id;
    otvory.push(
      <g key={`o${o.id}`}>
        <rect
          x={ir.x} y={ir.y} width={ir.w} height={ir.h}
          fill={
            isSel
              ? "hsl(350 80% 60% / 0.10)"
              : placing
                ? "hsl(350 80% 60% / 0.04)"
                : "transparent"
          }
          stroke={isSel ? SEL_STROKE : "transparent"}
          strokeWidth="2"
          className={placing ? "cursor-copy" : "cursor-pointer"}
          role="button"
          aria-label={
            placing
              ? `Vložiť komponent do otvoru ${Math.round(r.w)}×${Math.round(r.h)}`
              : `Otvor ${Math.round(r.w)}×${Math.round(r.h)}`
          }
          onClick={() =>
            placing && onPlace
              ? onPlace(o.id)
              : onSelect({ kind: "otvor", id: o.id })
          }
        />
        {isSel ? (
          <text
            x={ir.x + ir.w / 2} y={ir.y + 14}
            textAnchor="middle" fontSize="10"
            fill={SEL_STROKE} fontFamily="monospace"
            pointerEvents="none"
          >
            {Math.round(r.w)}×{Math.round(r.h)}
          </text>
        ) : null}
      </g>,
    );
  }
  walk(root);

  // nohy
  const nohyEls: JSX.Element[] = [];
  if (nohy.enabled && LH > 0) {
    const count = Math.max(2, Math.floor((nohy.pocetOverride ?? pocetNoh(W)) / 2));
    for (let i = 0; i < count; i++) {
      const nx = W * (0.1 + (i * 0.8) / Math.max(count - 1, 1));
      nohyEls.push(
        <rect
          key={`n${i}`}
          x={X(nx) - 3.5} y={Y(LH)} width={7} height={px(LH)}
          fill="hsl(210 8% 62%)" stroke={WOOD_EDGE} strokeWidth="0.5"
        />,
      );
    }
  }

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full rounded-md border bg-card" role="img" aria-label="Nárys korpusu">
      {nohyEls}
      {dosky}
      {medzisteny}
      {police}
      {otvory}
      {/* kóty */}
      <g stroke={SEL_STROKE} fill={SEL_STROKE} fontSize="11" fontFamily="monospace">
        <line x1={X(0)} y1={Y(0) + 16} x2={X(W)} y2={Y(0) + 16} strokeWidth="1" />
        <text x={X(W / 2)} y={Y(0) + 30} textAnchor="middle" stroke="none">{W}</text>
        <line x1={X(W) + 14} y1={Y(LH)} x2={X(W) + 14} y2={Y(LH + H)} strokeWidth="1" />
        <text x={X(W) + 18} y={Y(LH + H / 2)} stroke="none">{H}</text>
      </g>
    </svg>
  );
}

function BokorysSvg({
  params,
  root,
  rects,
}: {
  params: KorpusParams;
  root: Otvor;
  rects: Map<string, Rect>;
}): JSX.Element {
  const { sirka: W, vyska: H, hlbka: D, t, chrbat, nohy, presahy: pr } = params;
  const LH = nohy.enabled ? nohy.vyskaMm : 0;
  const pad = 30;
  const VW = 290;
  const VH = 340;
  const celoCtx = { t, innerW: W - 2 * t, innerH: H - 2 * t };

  const hlbkaMedzist = hlbkaMedzisteny(D, chrbat, t);

  // vodorovné medzisteny (rez) + zvislé (pohľad) + police + čelá
  // z otvorov — zber pred výpočtom extentov (predsadené čelá
  // rozširujú viewBox o 20 mm pred korpus)
  const policeRez: { y: number; hlbka: number; odsadenie: number }[] = [];
  const medzistenyVod: number[] = []; // y osi (vnútorné súradnice)
  const medzistenyZvis: { y: number; h: number }[] = [];
  const dvierkaRez: { y0: number; h: number; typ: TypCela }[] = [];
  const zasuvkyRez: {
    y0: number;
    h: number;
    pocet: number;
    vyskaBoku: number;
    typ: TypCela;
  }[] = [];
  function walk(o: Otvor): void {
    const r = rects.get(o.id);
    if (!r) return;
    if (o.delenie) {
      const d = o.delenie;
      const roz = d.smer === "vodorovne" ? r.h : r.w;
      const os =
        d.ref === "stred" ? roz / 2 : d.ref === "od_stropu" || d.ref === "od_boku_p" ? roz - d.mm : d.mm;
      if (os >= t / 2 && os <= roz - t / 2) {
        if (d.smer === "vodorovne") {
          medzistenyVod.push(r.y + os);
        } else {
          medzistenyZvis.push({ y: r.y, h: r.h });
        }
        walk(d.deti[0]);
        walk(d.deti[1]);
      }
      return;
    }
    // police za vloženými dvierkami: kratšie o 20 mm a posunuté dozadu
    const odsadenie = policeZaVlozenymi(o) ? POLICA_SETBACK_VLOZENE : 0;
    for (let i = 1; i <= o.police; i++) {
      policeRez.push({
        y: r.y + (r.h * i) / (o.police + 1),
        hlbka: D - odpocetPolice(chrbat, t) - odsadenie,
        odsadenie,
      });
    }
    if (o.dvierka) {
      const typ = normalizujTypCela(o.dvierka.typ);
      const { y0, vyska } = rozmerDvierok(r, o.dvierka, celoCtx);
      dvierkaRez.push({ y0, h: vyska, typ });
    }
    if (o.zasuvky && o.zasuvky.pocet > 0) {
      const typ = normalizujTypCela(o.zasuvky.celoTyp);
      const plocha = rozmerCelnejPlochy(r, typ, celoCtx);
      zasuvkyRez.push({
        y0: plocha.y0,
        h: plocha.h,
        pocet: o.zasuvky.pocet,
        vyskaBoku:
          o.zasuvky.vyskaBoku ??
          vyskaBokuZasuvky(r.h, o.zasuvky.pocet, typ),
        typ,
      });
    }
  }
  walk(root);
  const maPredsadene =
    dvierkaRez.some((d) => d.typ !== "vlozene") ||
    zasuvkyRez.some((z) => z.typ !== "vlozene");

  // extenty vrátane presahov: x = hĺbka (0 = predok), y = od podlahy
  const maxPredny = Math.max(pr.plat.predny, pr.dno.predny, pr.bok_l.predny, pr.bok_p.predny);
  const maxZadny = Math.max(pr.plat.zadny, pr.dno.zadny, pr.bok_l.zadny, pr.bok_p.zadny);
  const bokY0 = params.typ === "vlozeny_plat" ? LH : LH + t;
  const bokDlzka = params.typ === "vlozeny_plat" ? H : H - 2 * t;
  const bokDole = Math.max(pr.bok_l.a, pr.bok_p.a);
  const bokHore = Math.max(pr.bok_l.b, pr.bok_p.b);
  const minX = Math.min(-maxPredny, maPredsadene ? -PREDSADENIE_CELA : 0);
  const maxX = D + maxZadny;
  const minY = Math.min(0, bokY0 - bokDole);
  const maxY = Math.max(LH + H, bokY0 + bokDlzka + bokHore);
  const s = Math.min(
    (VW - 2 * pad) / Math.max(maxX - minX, 1),
    (VH - 2 * pad) / Math.max(maxY - minY, 1),
  );
  const X = (mm: number): number => pad + (mm - minX) * s;
  const Y = (mm: number): number => VH - pad - (mm - minY) * s;
  const px = (mm: number): number => mm * s;
  // NL výsuvu pre boxy zásuviek v reze (hĺbka boxu)
  const vysuvNl = vyberNlVysuvu(D);

  const hatchId = "kbHatch";
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full rounded-md border bg-card" role="img" aria-label="Bokorys korpusu">
      <defs>
        <pattern id={hatchId} width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill={WOOD} />
          <line x1="0" y1="0" x2="0" y2="6" stroke={WOOD_EDGE} strokeWidth="1.1" />
        </pattern>
      </defs>
      {/* bok v pozadí (s presahmi) */}
      <rect
        x={X(-Math.max(pr.bok_l.predny, pr.bok_p.predny))}
        y={Y(bokY0 + bokDlzka + bokHore)}
        width={px(D + Math.max(pr.bok_l.predny, pr.bok_p.predny) + Math.max(pr.bok_l.zadny, pr.bok_p.zadny))}
        height={px(bokDlzka + bokDole + bokHore)}
        fill={WOOD}
        opacity="0.15"
      />
      {/* zvislé medzisteny — pohľad na plochu */}
      {medzistenyZvis.map((m, i) => (
        <rect
          key={`mz${i}`}
          x={X(0)} y={Y(LH + t + m.y + m.h)}
          width={px(hlbkaMedzist)} height={px(m.h)}
          fill={WOOD} opacity="0.3"
        />
      ))}
      {/* dno + plát (rez, s presahmi) */}
      <rect
        x={X(-pr.dno.predny)} y={Y(LH + t)}
        width={px(D + pr.dno.predny + pr.dno.zadny)} height={px(t)}
        fill={`url(#${hatchId})`} stroke={WOOD_EDGE}
      />
      <rect
        x={X(-pr.plat.predny)} y={Y(LH + H)}
        width={px(D + pr.plat.predny + pr.plat.zadny)} height={px(t)}
        fill={`url(#${hatchId})`} stroke={WOOD_EDGE}
      />
      {/* vodorovné medzisteny (rez) */}
      {medzistenyVod.map((os, i) => (
        <rect
          key={`mv${i}`}
          x={X(0)} y={Y(LH + t + os + t / 2)}
          width={px(hlbkaMedzist)} height={px(t)}
          fill={`url(#${hatchId})`} stroke={WOOD_EDGE}
        />
      ))}
      {/* police — za vloženými dvierkami posunuté 20 mm dozadu */}
      {policeRez.map((p, i) => (
        <rect
          key={i}
          x={X(p.odsadenie)} y={Y(LH + t + p.y + t / 2)}
          width={px(p.hlbka)} height={px(t)}
          fill={`url(#${hatchId})`} stroke={WOOD_EDGE}
        />
      ))}
      {/* chrbát */}
      {chrbat === "hdf" ? (
        <rect x={X(D - 12)} y={Y(LH + H - 6)} width={Math.max(px(4), 2.5)} height={px(H - 12)} fill="hsl(33 25% 55%)" stroke={WOOD_EDGE} strokeWidth="0.5" />
      ) : chrbat === "sololit" ? (
        // pribíjaný sololit 4 mm na zadnej hrane, (V−10) × (Š−10)
        <rect x={X(D - 4)} y={Y(LH + H - 5)} width={Math.max(px(4), 2.5)} height={px(H - 10)} fill="hsl(33 25% 55%)" stroke={WOOD_EDGE} strokeWidth="0.5" />
      ) : chrbat === "ltd" ? (
        // vložený LTD chrbát medzi bokmi a plátom/dnom (V−2t)
        <rect x={X(D - t)} y={Y(LH + H - t)} width={px(t)} height={px(H - 2 * t)} fill={`url(#${hatchId})`} stroke={WOOD_EDGE} />
      ) : null}
      {/* nohy */}
      {nohy.enabled && LH > 0 ? (
        <>
          <rect x={X(D * 0.15) - 3} y={Y(LH)} width={6} height={px(LH)} fill="hsl(210 8% 62%)" />
          <rect x={X(D * 0.85) - 3} y={Y(LH)} width={6} height={px(LH)} fill="hsl(210 8% 62%)" />
        </>
      ) : null}
      {/* dvierka v reze — naložené 20 mm pred korpusom (vidno 2 mm
          medzeru pri hr. 18), vložené flush v otvore */}
      {dvierkaRez.map((dv, i) => {
        const x0 = dv.typ === "vlozene" ? 0 : -PREDSADENIE_CELA;
        return (
          <rect
            key={`dvr${i}`}
            data-testid="rez-dvierka"
            x={X(x0)} y={Y(LH + t + dv.y0 + dv.h)}
            width={px(t)} height={px(dv.h)}
            fill={WOOD} opacity="0.55" stroke={WOOD_EDGE}
          />
        );
      })}
      {/* zásuvky v reze — čelá vpredu + boxy s výsuvom */}
      {zasuvkyRez.map((za, i) => {
        const n = za.pocet;
        const vyskaCela = (za.h - (n - 1) * MEDZERA_CIEL) / n;
        const celoX = za.typ === "vlozene" ? 0 : -PREDSADENIE_CELA;
        const boxHlbka = vysuvNl ?? D - 40;
        const prvky: JSX.Element[] = [];
        for (let j = 0; j < n; j++) {
          const cy = LH + t + za.y0 + j * (vyskaCela + MEDZERA_CIEL);
          prvky.push(
            <g key={`zar${i}_${j}`} data-testid="rez-zasuvka">
              <rect
                x={X(celoX)} y={Y(cy + vyskaCela)}
                width={px(t)} height={px(vyskaCela)}
                fill={WOOD} opacity="0.55" stroke={WOOD_EDGE}
              />
              <rect
                x={X(t)} y={Y(cy + 20 + za.vyskaBoku)}
                width={px(boxHlbka)} height={px(za.vyskaBoku)}
                fill={WOOD} opacity="0.25" stroke={WOOD_EDGE}
                strokeDasharray="4 3"
              />
            </g>,
          );
        }
        return prvky;
      })}
      <g stroke={SEL_STROKE} fill={SEL_STROKE} fontSize="10" fontFamily="monospace">
        <line x1={X(0)} y1={Y(0) + 12} x2={X(D)} y2={Y(0) + 12} strokeWidth="1" />
        <text x={X(D / 2)} y={Y(0) + 24} textAnchor="middle" stroke="none">{D}</text>
      </g>
      <text x={X(0)} y={Y(LH + H) - 5} fontSize="9" fill="currentColor" opacity="0.55">← predok</text>
    </svg>
  );
}
